import React, { memo, useCallback, useMemo } from "react";
import { StyleSheet, Text, View } from "../platformComponents";
import { AsInput } from "./AsInput";
import { KycSelect } from "./KycSelect";
import { AsFormItemLayout } from "./AsFormItemLayout";
import type { IFormItemLayout, TListedEntity } from "@adgm/cross-models";
import { CommonRadio } from "../common";
import { colors, fontFamily, fontSize, margin, padding } from "../styles/common";
import { i18nCrossUtil, IS_WEB, kycCountrySelectOptionList } from "@adgm/libs";
import { KycLabelElement } from "./KycLabelElement";
import { isEqual } from "lodash-es";

interface IProps extends IFormItemLayout<TListedEntity> {
  value: TListedEntity;
  onChange: (value: TListedEntity | Record<string, never>) => void;
}

/**
 * 自定义比较函数，用于优化memo性能
 * @description 对value数组进行深度比较，其他props使用浅比较
 * @param prevProps - 前一次props
 * @param nextProps - 下一次props
 * @returns boolean - 是否相等
 */
const areEqual = (prevProps: IProps, nextProps: IProps) => {
  return (
    isEqual(prevProps.value, nextProps.value) &&
    prevProps.label === nextProps.label &&
    prevProps.error === nextProps.error &&
    prevProps.sequenceNumber === nextProps.sequenceNumber &&
    prevProps.tip === nextProps.tip &&
    prevProps.hideErrorSpace === nextProps.hideErrorSpace &&
    prevProps.required === nextProps.required &&
    prevProps.style === nextProps.style
  );
};

/**
 * 上市实体表单组件
 * 用于输入上市实体的名称、国家和交易所信息
 */
export const ListedEntityForm: React.FC<IProps> = memo(
  ({ value, onChange, error, label, tip, hideErrorSpace, sequenceNumber, style, required }) => {
    const crossIntl = i18nCrossUtil.t();

    const handleChange = useCallback(
      (field: keyof TListedEntity, newValue: string | string[]) => {
        onChange({
          ...value,
          [field]: newValue,
        } as TListedEntity);
      },
      [onChange, value],
    );

    const onValueChange = useCallback(() => {
      onChange({
        name: value?.name ?? "",
        country: value?.country ?? "",
        exchange: value?.exchange ?? "",
      });
    }, [onChange, value]);

    const isListedEntityEmpty = useMemo(() => {
      return value && (Object.keys(value).length === 0 || JSON.stringify(value) === "{}");
    }, [value]);

    return (
      <AsFormItemLayout
        label={label}
        error={error}
        tip={tip}
        hideErrorSpace={hideErrorSpace}
        required={required}
        labelElement={
          <KycLabelElement label={label} sequenceNumber={sequenceNumber} required={required} />
        }
        style={style}>
        <View style={{ flex: 1 }}>
          <CommonRadio
            style={{ marginBottom: 28 }}
            value={value}
            checked={isListedEntityEmpty}
            label={crossIntl["kyc.option.no.label"]}
            onChange={() => onChange({})}
          />
          <CommonRadio
            style={{ marginBottom: 28 }}
            value={value}
            checked={value && !isListedEntityEmpty}
            label={crossIntl["kyc.other.input"]}
            onChange={onValueChange}
          />
          <View style={styles.listedEntityForm}>
            <View style={styles.listedEntityFormContent}>
              <AsInput
                error={error}
                style={{
                  backgroundColor: IS_WEB ? colors.mainBackgroundWeb : colors.mainBackground,
                }}
                label={
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <Text style={styles.requiredIcon}>*</Text>
                    <Text style={styles.listedEntityFormLabel}>
                      {crossIntl["kyc.institution.basicInformation.listedEntity.name"]}
                    </Text>
                  </View>
                }
                onValueChange={name => handleChange("name", name)}
                value={value?.name || ""}
              />
              <KycSelect
                className="listedEntityFormSelect"
                error={error}
                label={
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <Text style={styles.requiredIcon}>*</Text>
                    <Text style={styles.listedEntityFormLabel}>
                      {crossIntl["kyc.institution.basicInformation.listedEntity.country"]}
                    </Text>
                  </View>
                }
                onValueChange={country => handleChange("country", country)}
                options={kycCountrySelectOptionList("nationality")}
                value={value?.country || ""}
                showSearch
              />
            </View>

            <AsInput
              error={error}
              style={{ backgroundColor: IS_WEB ? colors.mainBackgroundWeb : colors.mainBackground }}
              label={
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <Text style={styles.requiredIcon}>*</Text>
                  <Text style={styles.listedEntityFormLabel}>
                    {crossIntl["kyc.institution.basicInformation.listedEntity.exchange"]}
                  </Text>
                </View>
              }
              onValueChange={exchange => handleChange("exchange", exchange)}
              value={value?.exchange || ""}
            />
          </View>
        </View>
      </AsFormItemLayout>
    );
  },
  areEqual,
);

const styles = StyleSheet.create({
  listedEntityForm: {
    flex: 1,
    padding: IS_WEB ? padding.p32 : padding.p16,
    backgroundColor: IS_WEB ? colors.grey22 : colors.mainBackground,
    marginLeft: IS_WEB ? 0 : padding.p24,
  },
  listedEntityFormContent: {
    flex: 1,
    flexDirection: IS_WEB ? "row" : "column",
    alignItems: IS_WEB ? "center" : "stretch",
    justifyContent: "space-between",
    gap: IS_WEB ? 48 : 16,
  },
  listedEntityFormLabel: {
    fontSize: IS_WEB ? fontSize.f14 : fontSize.f12,
    fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
  },
  requiredIcon: { color: colors.red14, marginRight: margin.m8, fontSize: fontSize.f12 },
});
